import { useCallback, useMemo, useRef, useState } from "react";
import { Button, Card, CardBody } from "reactstrap";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ColumnDef } from "@tanstack/react-table";
import { DateOnly } from "components/common/Date";
import type {
  AmcVisit,
  CompleteAmcVisitRequest,
} from "features/AmcVisitLogs/types/amc-visit.types";
import {
  cancelAmcVisit,
  completeAmcVisit,
  getAmcVisits,
  startAmcVisit,
} from "features/AmcVisitLogs/services/AmcVisitService";
import { addExtraAmcVisit } from "features/Amc/services/AmcService";
import type { AddExtraAmcVisitRequest } from "features/Amc/types/amc.types";
import { CodeCell, IndexColumn } from "components/common/columns";
import StatusBadge from "components/common/StatusBadge";
import {
  AmcVisitKind,
  AmcVisitStatus,
  amcVisitKindLabelMap,
  amcVisitStatusMap,
} from "features/AmcVisitLogs/constants/amc-visit.constants";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import CancelModal from "features/AmcVisitLogs/CancelModal";
import CompleteVisitModal from "features/AmcVisitLogs/CompleteVisitModal";
import AddExtraAmcVisitModal from "features/Amc/components/AddExtraAmcVisitModal";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import {
  canReassignAmcVisit,
  shouldShowAmcVisitAssignmentActions,
} from "features/AmcVisitLogs/utils/amcVisitActionPolicy";
import { useAmcContractOverview } from "./AmcContractOverviewContext";
import { AmcContractStatus } from "features/Amc/constants/amc-contract.constants";
import AmcVisitAssigneesAvatarGroup from "features/AmcVisitLogs/VisitLogList/AmcVisitAssigneesAvatarGroup";

export default function AmcContractVisitLogsTab() {
  const { contract, refetch } = useAmcContractOverview();
  const { can } = useAccess();
  const canReadVisit = can(Permissions.AMC_VISIT.READ);
  const canUpdateVisit = can(Permissions.AMC_VISIT.UPDATE);
  const canCompleteVisit = can(Permissions.AMC_VISIT.COMPLETE);
  const canCancelVisit = can(Permissions.AMC_VISIT.CANCEL);
  const canAssign = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canReassign = can(Permissions.WORK_ASSIGNMENT.UPDATE);
  const canAddExtraVisit =
    contract?.status === AmcContractStatus.ACTIVE &&
    can(Permissions.AMC.UPDATE);

  const tableRef = useRef<() => void>(null);
  const [amcVisit, setAmcVisit] = useState<AmcVisit>();
  const [cancelModal, setCancelModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [extraVisitModal, setExtraVisitModal] = useState(false);
  const [extraVisitSubmitting, setExtraVisitSubmitting] = useState(false);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormVisit, setAssignFormVisit] = useState<AmcVisit | null>(null);
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);
  const [startBusyId, setStartBusyId] = useState<number | null>(null);

  const contractId = contract?.id;

  const refreshTable = useCallback(() => {
    tableRef.current?.();
  }, []);

  const listRequestParams = useMemo(
    () =>
      contractId != null && Number.isInteger(contractId) && contractId > 0
        ? { amcId: contractId }
        : {},
    [contractId],
  );

  const onClickCancel = useCallback((record: AmcVisit) => {
    setAmcVisit(record);
    setCancelModal(true);
  }, []);

  const onClickComplete = useCallback((record: AmcVisit) => {
    setAmcVisit(record);
    setCompleteModal(true);
  }, []);

  const onClickStartVisit = useCallback(
    async (record: AmcVisit) => {
      setStartBusyId(record.id);
      try {
        const response = await startAmcVisit(record.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
          await refetch();
        }
      } catch (err: unknown) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not start visit",
        );
      } finally {
        setStartBusyId(null);
      }
    },
    [refetch],
  );

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormVisit(null);
    setAssignFormIsReassign(false);
  }, []);

  const openAssign = useCallback((record: AmcVisit) => {
    setAssignFormIsReassign(false);
    setAssignFormVisit(record);
    setAssignFormOpen(true);
  }, []);

  const openReassign = useCallback((record: AmcVisit) => {
    if (!canReassignAmcVisit(record.status)) {
      toast.info(
        "Completed, cancelled, or skipped visits cannot be reassigned.",
      );
      return;
    }
    setAssignFormIsReassign(true);
    setAssignFormVisit(record);
    setAssignFormOpen(true);
  }, []);

  const handleCancel = async (reason?: string) => {
    if (!amcVisit) return;
    try {
      const response = await cancelAmcVisit(amcVisit.id, reason);
      if (response.success) {
        toast.success(response.message);
        setCancelModal(false);
        tableRef.current?.();
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not cancel visit",
      );
    }
  };

  const handleComplete = async (body: CompleteAmcVisitRequest) => {
    if (!amcVisit) return;
    try {
      const response = await completeAmcVisit(amcVisit.id, body);
      if (response.success) {
        toast.success(response.message);
        tableRef.current?.();
      }
      setCompleteModal(false);
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not complete visit",
      );
    }
  };

  const handleAddExtraVisit = async (body: AddExtraAmcVisitRequest) => {
    if (contractId == null || !Number.isInteger(contractId) || contractId <= 0)
      return;
    setExtraVisitSubmitting(true);
    try {
      const response = await addExtraAmcVisit(contractId, body);
      if (response.success) {
        toast.success(response.message);
        setExtraVisitModal(false);
        refreshTable();
        await refetch();
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not add extra visit",
      );
    } finally {
      setExtraVisitSubmitting(false);
    }
  };

  const columns: ColumnDef<AmcVisit>[] = useMemo(
    () => [
      IndexColumn<AmcVisit>(),
      {
        header: "Code",
        cell: ({ row }) =>
          canReadVisit ? (
            <Link
              to={`/amc-management/visit-logs/${row.original.id}`}
              className="text-decoration-none link"
            >
              <CodeCell code={row.original.code} />
            </Link>
          ) : (
            <CodeCell code={row.original.code} />
          ),
        accessorKey: "code",
        enableColumnFilter: false,
      },
      {
        header: "Visit #",
        accessorKey: "visitNumber",
        enableColumnFilter: false,
        cell: ({ row }) => row.original.visitNumber ?? "—",
      },
      {
        header: "Kind",
        accessorKey: "visitKind",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const kind = row.original.visitKind;
          if (!kind) return "—";
          return kind === AmcVisitKind.EXTRA ? (
            <span className="badge bg-warning-subtle text-warning">
              {amcVisitKindLabelMap[kind]}
            </span>
          ) : (
            <span className="text-muted small">{amcVisitKindLabelMap[kind]}</span>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <StatusBadge value={row.original.status} map={amcVisitStatusMap} />
        ),
      },
      {
        header: "Scheduled on",
        accessorKey: "scheduledAt",
        enableColumnFilter: false,
        cell: ({ row }) => <DateOnly date={row.original.scheduledAt} />,
      },
      {
        header: "Assigned",
        id: "assigned",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const canAssignMore =
            shouldShowAmcVisitAssignmentActions(r.status) && canAssign;
          return (
            <AmcVisitAssigneesAvatarGroup
              visit={r}
              assignments={r.workAssignments}
              visitId={r.id}
              canReadDetail={canReadVisit}
              canAssignMore={canAssignMore}
              onOpenAssign={openAssign}
            />
          );
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const base = `/amc-management/visit-logs`;
          const isEditable = r.status === AmcVisitStatus.PLANNED;
          const isCancellable = r.status === AmcVisitStatus.PLANNED;
          const isActionable = shouldShowAmcVisitAssignmentActions(r.status);
          const showAssignmentItems = isActionable && (canAssign || canReassign);
          const showEdit = canUpdateVisit && isEditable;
          const showCancel = canCancelVisit && isCancellable;
          const hasMenuItems =
            canReadVisit ||
            showEdit ||
            canCompleteVisit ||
            showCancel ||
            showAssignmentItems;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }

          const hasTopItems = canReadVisit || showEdit;
          const hasItemsBeforeStart = hasTopItems || showAssignmentItems;
          const hasItemsBeforeComplete =
            hasTopItems || showAssignmentItems || canUpdateVisit;
          const hasItemsBeforeCancel =
            hasTopItems ||
            showAssignmentItems ||
            canUpdateVisit ||
            canCompleteVisit;

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Visit log actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadVisit ? (
                  <DropdownItem tag={Link} to={`${base}/${r.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {showEdit ? (
                  <DropdownItem tag={Link} to={`${base}/edit/${r.id}`}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showAssignmentItems ? (
                  <>
                    {hasTopItems ? <DropdownItem divider /> : null}
                    {canAssign ? (
                      <DropdownItem onClick={() => openAssign(r)}>
                        <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                        Assign user
                      </DropdownItem>
                    ) : null}
                    {canReassign ? (
                      <DropdownItem onClick={() => openReassign(r)}>
                        <i className="ri-user-shared-line align-bottom me-2 text-muted" />
                        Reassign
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
                {canUpdateVisit && r.status === AmcVisitStatus.PLANNED ? (
                  <>
                    {hasItemsBeforeStart ? <DropdownItem divider /> : null}
                    <DropdownItem
                      disabled={startBusyId === r.id}
                      onClick={() => void onClickStartVisit(r)}
                    >
                      <i className="ri-play-line align-bottom me-2 text-muted" />
                      {startBusyId === r.id ? "Starting…" : "Start visit"}
                    </DropdownItem>
                  </>
                ) : null}
                {canCompleteVisit && r.status === AmcVisitStatus.IN_PROGRESS ? (
                  <>
                    {hasItemsBeforeComplete ? <DropdownItem divider /> : null}
                    <DropdownItem onClick={() => onClickComplete(r)}>
                      <i className="ri-check-double-line align-bottom me-2 text-muted" />
                      Complete visit
                    </DropdownItem>
                  </>
                ) : null}
                {showCancel ? (
                  <>
                    {hasItemsBeforeCancel ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickCancel(r)}
                    >
                      <i className="ri-close-circle-line align-bottom me-2" />
                      Cancel visit
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      canReadVisit,
      canUpdateVisit,
      canCompleteVisit,
      canCancelVisit,
      canAssign,
      canReassign,
      onClickCancel,
      onClickComplete,
      onClickStartVisit,
      openAssign,
      openReassign,
      startBusyId,
    ],
  );

  if (contractId == null) {
    return null;
  }

  return (
    <>
      <CancelModal
        show={cancelModal}
        onCancelClick={handleCancel}
        onCloseClick={() => setCancelModal(false)}
        record={amcVisit}
      />
      <CompleteVisitModal
        show={completeModal}
        onCompleteClick={handleComplete}
        onCloseClick={() => setCompleteModal(false)}
        record={amcVisit}
      />
      <AddExtraAmcVisitModal
        isOpen={extraVisitModal}
        toggle={() => setExtraVisitModal(false)}
        contractLabel={contract?.code ?? null}
        submitting={extraVisitSubmitting}
        onConfirm={handleAddExtraVisit}
      />
      <WorkAssignmentForm
        show={assignFormOpen && assignFormVisit != null}
        sourceType={WorkAssignmentSourceType.AMC_VISIT}
        sourceId={assignFormVisit?.id ?? 0}
        sourceLabel={
          assignFormVisit
            ? assignFormVisit.code
              ? `AMC visit ${assignFormVisit.code}`
              : `AMC visit #${assignFormVisit.id}`
            : undefined
        }
        isReassign={assignFormIsReassign}
        onCloseClick={closeAssignmentForm}
        onSuccess={refreshTable}
      />
      <Card>
        <CardBody>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <h6 className="mb-0 text-uppercase">Visit logs</h6>
            {canAddExtraVisit ? (
              <Button
                color="primary"
                size="sm"
                type="button"
                className="btn-label"
                onClick={() => setExtraVisitModal(true)}
              >
                <i className="ri-add-line label-icon align-middle fs-16 me-1" />
                Extra visit
              </Button>
            ) : null}
          </div>

          <TableContainer
            columns={columns}
            fetchApi={getAmcVisits}
            pageSize={10}
            globalSearch={true}
            refreshRef={tableRef}
            requestParams={listRequestParams}
            divClass="table-responsive table-card mb-0"
            tableClass="align-middle table-nowrap mb-0"
            theadClass="table-light text-muted"
          />
        </CardBody>
      </Card>
    </>
  );
}
